import React from 'react'
import { localizedStrings } from '@core/strings'
import { Box, Flex, Text } from '@fundamentals'
import { Button } from '@common/material'
import ConfirmModal from '@common/modal/ConfirmModal'
import { useModal } from 'webapp/components/common/GlobalModal'
import { useGetProfile } from '@core/react-query/features/profile/hooks/useGetProfile'
import { useRespondToBriefing } from '@core/react-query/features/permits/permit'
import { useToast } from '@core/toast'
import { Permit } from '@core/entities/permit/Permit'
import { ParticipantRole } from '@core/entities/participant/Participant'
import { ActionCard } from '@elements'
import { AcceptBriefingModal } from '../modals/AcceptBriefingModal'

type RespondToBriefingCardProps = {
  permit: Permit
}

export const RespondToBriefingCard: React.FC<RespondToBriefingCardProps> = ({
  permit,
}) => {
  const { showModal, hideModal } = useModal()
  const { data: profile } = useGetProfile()
  const respondToBriefing = useRespondToBriefing()
  const toast = useToast()

  const handleOnDecline = (role: ParticipantRole) => {
    showModal(
      <ConfirmModal
        confirmText={localizedStrings.decline}
        onConfirm={() => {
          respondToBriefing.mutate(
            {
              permitId: permit.id,
              templateVersionRoleId: role.roleId,
              decision: 'DECLINE',
            },
            {
              onSuccess: () => {
                toast.success(localizedStrings.briefingDeclined)
                hideModal()
              },
              onError: (error) => toast.error(String(error.message)),
            },
          )
        }}
        onDismiss={hideModal}
      >
        {localizedStrings.declineBriefingWarning}
      </ConfirmModal>,
    )
  }

  if (!permit.hasPermission('RESPOND_TO_BRIEFING')) return null

  const participant = permit.participants?.find(
    ({ userId }) => userId === profile?.id,
  )
  const pendingRoles = participant?.getPendingAcceptanceRoles() ?? []

  if (!pendingRoles.length) return null

  return (
    <ActionCard
      title={localizedStrings.permitAcceptanceRequired}
      description={localizedStrings.briefingAcceptanceDescription}
      BottomComponent={pendingRoles.map((role) => (
        <Box key={role.roleId}>
          {pendingRoles.length > 1 && (
            <Text sx={{ fontSize: '14px', mb: 1 }}>{role.name}</Text>
          )}
          <Flex gap={1}>
            <Button
              variant='outlined'
              fullWidth
              data-test='decline-briefing'
              onClick={() => handleOnDecline(role)}
            >
              {localizedStrings.decline}
            </Button>
            <Button
              variant='contained'
              fullWidth
              data-test='accept-briefing'
              onClick={() =>
                showModal(<AcceptBriefingModal permit={permit} role={role} />)
              }
            >
              {localizedStrings.accept}
            </Button>
          </Flex>
        </Box>
      ))}
      sx={{ mb: 1.5, borderColor: 'primary.main', borderWidth: 2 }}
    />
  )
}
